/**
 * ARCHIVED ORGANIZATIONS SECTION
 *
 * Displayed below the active organizations table in the ops portal dashboard.
 * Shows all organizations currently archived or archive_pending in the main app,
 * with a Reactivate button for each. Expandable per-location list shows
 * ARCHIVED vs PENDING status for each location.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Archive, RefreshCw, ChevronDown, ChevronUp, ChevronRight, Loader2, MapPin } from 'lucide-react';
import type { ArchivedOrg } from '@/hooks/use-main-app-archived-orgs';
import { ReactivateOrgDialog } from '@/components/reactivate-org-dialog';

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface ArchivedOrganizationsSectionProps {
  orgs: ArchivedOrg[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function ArchivedOrganizationsSection({ orgs, loading, error, refetch }: ArchivedOrganizationsSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedOrgIds, setExpandedOrgIds] = useState<Set<string>>(new Set());
  const [reactivateTarget, setReactivateTarget] = useState<ArchivedOrg | null>(null);

  const toggleOrgLocations = (orgId: string) => {
    setExpandedOrgIds((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) {
        next.delete(orgId);
      } else {
        next.add(orgId);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className='flex items-center gap-2 text-sm text-gray-500 py-2'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading archived organizations…
      </div>
    );
  }

  if (error) {
    return (
      <div className='text-sm text-red-600 py-2'>
        Failed to load archived organizations: {error.message}
      </div>
    );
  }

  if (orgs.length === 0) {
    return null; // Nothing to show
  }

  return (
    <>
      <Card className='border-gray-200 dark:border-gray-700'>
        <CardHeader
          className='cursor-pointer select-none'
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <div className='flex items-center justify-between'>
            <CardTitle className='flex items-center gap-2 text-gray-700 dark:text-gray-300 text-base'>
              <Archive className='h-4 w-4' />
              Archived Organizations
              <Badge
                variant='secondary'
                className='ml-1 text-xs bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
              >
                {orgs.length}
              </Badge>
            </CardTitle>
            <div className='flex items-center gap-2'>
              <Button
                variant='ghost'
                size='sm'
                onClick={(e) => { e.stopPropagation(); refetch(); }}
                className='text-xs text-gray-500'
              >
                <RefreshCw className='h-3 w-3 mr-1' />
                Refresh
              </Button>
              {isExpanded ? (
                <ChevronUp className='h-4 w-4 text-gray-400' />
              ) : (
                <ChevronDown className='h-4 w-4 text-gray-400' />
              )}
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className='pt-0'>
            <div className='divide-y divide-gray-100 dark:divide-gray-800'>
              {orgs.map((org) => {
                const isOrgExpanded = expandedOrgIds.has(org.id);
                const archivedCount = org.archived_locations.filter((l) => l.archived_at).length;
                const pendingCount = org.archived_locations.length - archivedCount;

                return (
                  <div key={org.id}>
                    <div className='flex items-center justify-between py-3 gap-4'>
                      {/* Org identity */}
                      <div className='flex items-center gap-3 min-w-0'>
                        {org.logo_url ? (
                          <div className='w-8 h-8 rounded overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800'>
                            <img
                              src={org.logo_url}
                              alt={`${org.name} logo`}
                              className='w-full h-full object-cover'
                            />
                          </div>
                        ) : (
                          <Building2 className='h-5 w-5 text-gray-400 shrink-0' />
                        )}
                        <div className='min-w-0'>
                          <p className='font-medium text-gray-900 dark:text-gray-100 truncate'>
                            {org.name}
                          </p>
                          {org.archived_locations.length > 0 ? (
                            <button
                              type='button'
                              onClick={() => toggleOrgLocations(org.id)}
                              className='flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
                            >
                              {isOrgExpanded ? (
                                <ChevronDown className='h-3 w-3' />
                              ) : (
                                <ChevronRight className='h-3 w-3' />
                              )}
                              {org.archived_locations.length} location{org.archived_locations.length !== 1 ? 's' : ''}
                              {pendingCount > 0 && archivedCount > 0 && (
                                <span className='text-gray-400 dark:text-gray-500'>
                                  ({archivedCount} archived, {pendingCount} pending)
                                </span>
                              )}
                              {org.archived_at ? <span className='text-gray-400 dark:text-gray-500'>· {formatDate(org.archived_at)}</span> : ''}
                            </button>
                          ) : (
                            <p className='text-xs text-gray-500 dark:text-gray-400'>
                              No locations
                              {org.archived_at ? ` · ${formatDate(org.archived_at)}` : ''}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Status + action */}
                      <div className='flex items-center gap-3 shrink-0'>
                        {org.status === 'archive_pending' ? (
                          <Badge className='bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 text-xs'>
                            ARCHIVE PENDING
                          </Badge>
                        ) : (
                          <Badge className='bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 text-xs'>
                            ARCHIVED
                          </Badge>
                        )}
                        <Button
                          size='sm'
                          variant='outline'
                          className='text-green-700 border-green-300 hover:bg-green-50 dark:text-green-400 dark:border-green-700 dark:hover:bg-green-950/20'
                          onClick={() => setReactivateTarget(org)}
                        >
                          <RefreshCw className='h-3.5 w-3.5 mr-1.5' />
                          Reactivate
                        </Button>
                      </div>
                    </div>

                    {/* Expanded location list */}
                    {isOrgExpanded && org.archived_locations.length > 0 && (
                      <div className='ml-11 mb-3 rounded-md border border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/30'>
                        <div className='divide-y divide-gray-100 dark:divide-gray-800'>
                          {org.archived_locations.map((loc) => (
                            <div
                              key={loc.id}
                              className='flex items-center gap-2.5 px-3 py-2'
                            >
                              {loc.logo_url ? (
                                <div className='w-5 h-5 rounded overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800'>
                                  <img
                                    src={loc.logo_url}
                                    alt={loc.name}
                                    className='w-full h-full object-cover'
                                  />
                                </div>
                              ) : (
                                <MapPin className='h-3.5 w-3.5 text-gray-400 shrink-0' />
                              )}
                              <span className='text-sm text-gray-700 dark:text-gray-300 truncate flex-1'>
                                {loc.name}
                              </span>
                              {loc.archived_at ? (
                                <Badge className='bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 text-[10px] px-1.5 py-0'>
                                  ARCHIVED
                                </Badge>
                              ) : (
                                <Badge className='bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 text-[10px] px-1.5 py-0'>
                                  PENDING
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Reactivate dialog */}
      {reactivateTarget && (
        <ReactivateOrgDialog
          open={!!reactivateTarget}
          onOpenChange={(open) => { if (!open) setReactivateTarget(null); }}
          organizationId={reactivateTarget.id}
          organizationName={reactivateTarget.name}
          // archivedLocationCount={reactivateTarget.archived_location_count}
          archivedLocations={reactivateTarget.archived_locations}
          orgStatus={reactivateTarget.status}
          onSuccess={refetch}
        />
      )}
    </>
  );
}
